import React, { useContext, useCallback, useReducer, useEffect } from 'react';
import { editCredentialsInitialState, EditCredentialsContext, TTypeOfModalWindow } from './editCredentialsContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { editCredentialsReducer } from './editCredentialsReducer';
import { EditCredentialsActionTypes } from './editCredentialsActions';
import { IGetCredentialsInfo, IUpdateCredentials } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getCredentialsInfo } from 'backend/credentials';
import { useNamedHeader } from 'hooks/useNamedHeader';

export const EditCredentialsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editCredentialsReducer, editCredentialsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const credentialID = params.get('credentialID');

	const { response, loading, refetch } = useFetchData<IGetCredentialsInfo, string>(
		credentialID ? getCredentialsInfo : null,
		credentialID || '',
	);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditCredentialsActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditCredentialsActionTypes.SET_ALL_DATA,
				payload: {
					credentialID: response.data.id,
					credentialType: response.data.type,
					credentialName: response.data.name || '',
					apiKey: response.data.credentials.apiKey || '',
					apiSecret: response.data.credentials.apiSecret || '',
				},
			});
			dispatch({
				type: EditCredentialsActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						credentialType: response.data.type,
						credentialName: response.data.name || '',
						apiKey: response.data.credentials.apiKey || '',
						apiSecret: response.data.credentials.apiSecret || '',
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditCredentialsActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeCredentialName = useCallback((value: string) => {
		dispatch({
			type: EditCredentialsActionTypes.CHANGE_CREDENTIAL_NAME,
			payload: {
				credentialName: value,
			},
		});
	}, []);

	const changeApiKey = useCallback((value: string) => {
		dispatch({
			type: EditCredentialsActionTypes.CHANGE_API_KEY,
			payload: {
				apiKey: value,
			},
		});
	}, []);

	const changeApiSecret = useCallback((value: string) => {
		dispatch({
			type: EditCredentialsActionTypes.CHANGE_API_SECRET,
			payload: {
				apiSecret: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateCredentials>) => {
		dispatch({
			type: EditCredentialsActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditCredentialsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditCredentialsActionTypes.RESET_FIELDS }), []);

	return (
		<EditCredentialsContext.Provider
			value={{
				refetchData,
				changeCredentialName,
				changeApiKey,
				changeApiSecret,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				credentialID: state.credentialID,
				credentialType: state.credentialType,
				credentialName: state.credentialName,
				apiKey: state.apiKey,
				apiSecret: state.apiSecret,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditCredentialsContext.Provider>
	);
};
